import { useState, useMemo } from 'react';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useContratoAdicional } from '@/hooks/useFuncoesAdicionais';
import { SoOsMeusToggle } from '@/components/kanban/triagem/SoOsMeusToggle';
import { PackageCheck, ArrowUpRight, Clock, Rocket, Send, ShieldAlert, Phone, X } from 'lucide-react';
import { ManterFilaDialog } from '@/components/kanban/triagem/ManterFilaDialog';
import { toast } from '@/hooks/use-toast';
import { ehDoSegmento, rotuloSegmento, useSegmentoTriagem } from '@/components/kanban/triagem/segmentoTriagem';
import { TriagemShell } from '@/components/kanban/triagem/TriagemShell';
import { TRIAGEM_TH } from '@/components/kanban/triagem/triagemTabela';
import { useLinhasOcupadas } from '@/components/kanban/triagem/acaoTriagem';
import { useTriagemPedidoFechado, type PedidoTriagemFechado } from './useTriagemPedidoFechado';
import { TriagemPedidoFechadoRow } from './TriagemPedidoFechadoRow';
import { PegarPedidoDialog } from './PegarPedidoDialog';
import { SemBiometriaDialog } from '@/components/kanban/criacao/SemBiometriaDialog';
import { ConferenciaTabelaRenovacao, useConferenciaTabelaRenovacao } from './ConferenciaTabelaRenovacao';
import { separarAparelho, useFlagsFaturamento } from '@/components/kanban/triagem/flagsFaturamento';

interface TriagemPedidoFechadoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TriagemPedidoFechadoModal({ isOpen, onClose }: TriagemPedidoFechadoModalProps) {
  const {
    pedidos: pedidosTodos,
    isLoading,
    refetch,
    invalidateAllQueries,
    meusEsperandoCriacao,
    pegarECriar,
    auditoriaCpfParaInput,
    manterNoFim,
  } = useTriagemPedidoFechado();
  /* Só o pedido clicado fica ocupado; o resto da fila continua livre
     (`triagem/acaoTriagem.ts`). */
  const { ocupada, ocupar } = useLinhasOcupadas();
  const [pedidoManter, setPedidoManter] = useState<PedidoTriagemFechado | null>(null);

  /* ⛔ CNPJ e CPF NÃO SE MISTURAM NA FILA (Douglas 17/09/2026). A régua de
     entrada desta triagem é a MESMA de sempre — o que muda é a fila mostrar um
     segmento por vez, e os contadores do topo falarem do que está na tela.
     A separação é filtro, nunca coluna nova: nada troca de fila fora da
     triagem. Lei em `triagem/segmentoTriagem.ts`. */
  const { segmento, setSegmento } = useSegmentoTriagem();
  const { data: perfil } = useUserProfile();
  const meuId = perfil?.id ?? null;
  /* Quem é BKO Contrato pela função adicional (a supervisora, Douglas
     28/10/2026) abre nos pedidos dela e nos sem dono, com a mesma fila FIFO da
     BKO; em "Todos" volta a ser a visão da supervisora. */
  const { adicional } = useContratoAdicional();
  const [soOsMeusAdicional, setSoOsMeusAdicional] = useState(true);
  const filtraOsMeus = adicional && soOsMeusAdicional;
  const pedidos = useMemo(
    () => pedidosTodos.filter((r) => ehDoSegmento(r.tipo_pessoa, segmento)
      && (!filtraOsMeus || r.tipo_pessoa === 'PF' || !r.dono_id || r.dono_id === meuId)),
    [pedidosTodos, segmento, filtraOsMeus, meuId],
  );

  /* Distribuição do Contrato (Douglas 15/10/2026): o pedido CNPJ chega com
     dono no Pedido fechado. Para o BKO_CONTRATO a fila FIFO é a DELE: o 1º
     pedido dele fica liberado; o do colega e o sem dono ficam apagados. CPF
     segue fila comum. Supervisora e gestão continuam como antes. */
  const ehBkoContrato = perfil?.funcao === 'BKO_CONTRATO' || filtraOsMeus;
  const topoId = useMemo(() => {
    if (!ehBkoContrato) return pedidos[0]?.pedido_id ?? null;
    const primeiro = pedidos.find((p) => p.tipo_pessoa === 'PF' || p.dono_id === meuId);
    return primeiro?.pedido_id ?? null;
  }, [pedidos, ehBkoContrato, meuId]);

  const [pedidoPegar, setPedidoPegar] = useState<PedidoTriagemFechado | null>(null);
  const [pedidoAuditoria, setPedidoAuditoria] = useState<PedidoTriagemFechado | null>(null);

  const totalItens = pedidos.reduce((sum, p) => sum + p.itens.length, 0);
  const { data: conferencias = [] } = useConferenciaTabelaRenovacao(segmento);
  const temConferencia = segmento === 'PJ' && conferencias.length > 0;
  // Aparelho anda SEPARADO do faturamento — tem KPI próprio e não é venda de
  // linha. Some do R$ da fila, não some da tela. Régua em `flagsFaturamento.ts`.
  // Soma pelos ITENS (e não por `p.valor_total`), que é onde mora o produto.
  const flagsFat = useFlagsFaturamento();
  const { faturamento: totalValue, qtdAparelhos } = separarAparelho(
    pedidos.flatMap((p) => p.itens), flagsFat, (i) => i.produto, (i) => i.subtotal || 0,
  );

  return (
    <TriagemShell
      isOpen={isOpen}
      onFechar={onClose}
      Icone={PackageCheck}
      titulo="Triagem Pedido Fechado"
      contadores={{ pedidos: pedidos.length, itens: totalItens, valor: totalValue, aparelhos: qtdAparelhos }}
      segmento={segmento}
      onSegmento={setSegmento}
      cabecalhoExtra={adicional ? <SoOsMeusToggle soOsMeus={soOsMeusAdicional} onChange={setSoOsMeusAdicional} /> : undefined}
      sla={null}
      fifo={true}
      fifoLabel="Conferência FIFO — pegou, é seu"
      legenda={[
        { key: 'manter', Icone: Clock, label: 'Manter (fim da fila)' },
        { key: 'analisar', Icone: ArrowUpRight, label: 'Analisar' },
        { key: 'confer-ncia-bko-no-painel', Icone: ShieldAlert, label: 'Conferência BKO no painel' },
        { key: 'ligar-whatsapp-inbox', Icone: Phone, label: 'Ligar / WhatsApp Inbox' },
        { key: 'pegar-e-criar', Icone: Rocket, label: 'Pegar e criar' },
        { key: 'auditoria-cpf', Icone: Send, label: 'CPF: auditoria feita → Input' },
        { key: 'cancelar', Icone: X, label: 'Cancelar' },
      ]}
      isLoading={isLoading}
      onAtualizar={async () => { await refetch(); toast({ title: 'Dados atualizados', duration: 1500 }); }}
      vazio={
        totalItens === 0 && !temConferencia
          ? { titulo: '\n                Nenhum pedido pendente\n              ', descricao: '\n                Todos os pedidos fechados foram processados na triagem\n              ' }
          : undefined
      }
    >
            <ConferenciaTabelaRenovacao segmento={segmento} />
            {totalItens === 0 ? (
              <p className="px-4 py-3 text-center text-xs text-muted-foreground">Nenhum pedido fechado na fila. Falta só a tabela da renovação acima.</p>
            ) : (
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <thead className="sticky top-0 z-10 bg-background border-b border-border">
                <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                  <th className={`${TRIAGEM_TH} w-[44px]`}>Fila</th>
                  <th className={`${TRIAGEM_TH}`}>{rotuloSegmento(segmento)}</th>
                  <th className={`${TRIAGEM_TH}`}>Razão Social</th>
                  <th className={`${TRIAGEM_TH}`}>Consultor</th>
                  <th className={`${TRIAGEM_TH} w-[96px]`}>Dono</th>
                  <th className={`${TRIAGEM_TH} w-[90px]`}>Pedido</th>
                  <th className={`${TRIAGEM_TH}`}>Produto</th>
                  <th className={`${TRIAGEM_TH} w-[42px]`}>QTD</th>
                  <th className={`${TRIAGEM_TH}`}>Valor</th>
                  <th className={`${TRIAGEM_TH} w-[86px]`}>Conf. BKO</th>
                  <th className={`${TRIAGEM_TH}`}>Tempo</th>
                  <th className={`${TRIAGEM_TH} w-[70px]`}>Contato</th>
                  <th className={`${TRIAGEM_TH} w-[190px]`}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pedidos.map((pedido, index) => (
                  <TriagemPedidoFechadoRow
                    key={pedido.pedido_id}
                    pedido={pedido}
                    posicao={index}
                    isTopo={pedido.pedido_id === topoId}
                    meuId={meuId}
                    bloquearPorDono={ehBkoContrato}
                    isProcessing={ocupada(pedido.pedido_id)}
                    onPegar={setPedidoPegar}
                    onAuditoriaCpf={setPedidoAuditoria}
                    onAuditoriaCpfComData={(p, dataIso) => { void ocupar(p.pedido_id, () => auditoriaCpfParaInput({ pedido: p, dataIso })).catch(() => undefined); }}
                    onManterFimFila={setPedidoManter}
                    onAfterCancel={invalidateAllQueries}
                  />
                ))}
              </tbody>
            </table>
            )}

        <ManterFilaDialog
          open={!!pedidoManter}
          onOpenChange={(open) => { if (!open) setPedidoManter(null); }}
          numeroPedido={pedidoManter?.numero_pedido || ''}
          razaoSocial={pedidoManter?.razao_social || ''}
          isLoading={!!pedidoManter && ocupada(pedidoManter.pedido_id)}
          onConfirm={async ({ justificativa, prd }) => {
            if (!pedidoManter) return;
            try {
              await ocupar(pedidoManter.pedido_id, () => manterNoFim({ pedido: pedidoManter, justificativa, prd }));
              setPedidoManter(null);
            } catch { /* toast no hook */ }
          }}
        />
        <PegarPedidoDialog
          open={!!pedidoPegar}
          onOpenChange={(open) => { if (!open) setPedidoPegar(null); }}
          numeroPedido={pedidoPegar?.numero_pedido || ''}
          razaoSocial={pedidoPegar?.razao_social || ''}
          meusEsperando={meusEsperandoCriacao}
          isLoading={!!pedidoPegar && ocupada(pedidoPegar.pedido_id)}
          onConfirm={async () => {
            if (!pedidoPegar) return;
            try {
              await ocupar(pedidoPegar.pedido_id, () => pegarECriar(pedidoPegar));
              setPedidoPegar(null);
            } catch { /* toast no hook */ }
          }}
        />
        <SemBiometriaDialog
          open={!!pedidoAuditoria}
          onOpenChange={(open) => { if (!open) setPedidoAuditoria(null); }}
          destino="AUDITORIA CPF"
          numeroPedido={pedidoAuditoria?.numero_pedido || ''}
          razaoSocial={pedidoAuditoria?.razao_social || ''}
          isLoading={!!pedidoAuditoria && ocupada(pedidoAuditoria.pedido_id)}
          onConfirm={() => {
            if (!pedidoAuditoria) return;
            void ocupar(pedidoAuditoria.pedido_id, () => auditoriaCpfParaInput({ pedido: pedidoAuditoria }))
              .then(() => setPedidoAuditoria(null))
              .catch(() => undefined);
          }}
        />
    </TriagemShell>
  );
}
